import React, { useCallback, useEffect, useRef, useState } from "react";
import LockIcon from "@mui/icons-material/Lock";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { DEFAULT_MARGIN, isMarginValid } from "./margin";

/*
  The margin, edited in place from the summary bar.

  It reads as "Hidden" until clicked, because salespeople fill this out with
  the customer sitting beside them. Closing the editor — by button, Escape, or
  clicking away — always re-conceals it, so it can't be left on screen.
*/
const MarginChip = ({ formData, setFormData }) => {
  const [open, setOpen] = useState(false);
  const [useCustom, setUseCustom] = useState(
    () => Number(formData.margin) !== DEFAULT_MARGIN
  );
  const containerRef = useRef(null);
  // What to fall back to if the editor closes on a half-typed value.
  const restoreRef = useRef(DEFAULT_MARGIN);

  const margin = formData.margin;
  const invalid = !isMarginValid(margin);

  const openEditor = () => {
    restoreRef.current = isMarginValid(margin) ? margin : DEFAULT_MARGIN;
    setUseCustom(Number(margin) !== DEFAULT_MARGIN);
    setOpen(true);
  };

  const closeEditor = useCallback(() => {
    // Read through the updater rather than the closure so an outside-click
    // handler can't revert a value the user just fixed.
    setFormData((prev) =>
      isMarginValid(prev.margin)
        ? prev
        : { ...prev, margin: restoreRef.current }
    );
    setOpen(false);
  }, [setFormData]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        closeEditor();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeEditor();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, closeEditor]);

  const handleMarginChange = (raw) => {
    // Stay clearable while typing; closeEditor catches anything invalid.
    if (raw === "") {
      setFormData((prev) => ({ ...prev, margin: "" }));
      return;
    }
    const value = Number(raw);
    if (Number.isNaN(value)) return;
    setFormData((prev) => ({ ...prev, margin: value }));
  };

  const segmentClasses = (active) =>
    `flex-1 rounded-full px-2 py-1.5 text-xs transition ${
      active
        ? "app-accent bg-sky-500/15 font-semibold"
        : "app-muted hover:bg-[color:var(--app-hover)]"
    }`;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => (open ? closeEditor() : openEditor())}
        aria-expanded={open}
        className={`app-surface flex items-center gap-2 rounded-xl border px-3 py-1.5 text-left transition hover:border-sky-500/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
          open ? "border-sky-500 ring-1 ring-sky-500" : ""
        }`}
      >
        <span className="min-w-0">
          <span className="app-muted block text-[10px] font-semibold uppercase tracking-wider">
            Margin
          </span>
          <span className="flex items-center gap-1">
            {!open && (
              <LockIcon className="app-muted shrink-0" sx={{ fontSize: 11 }} />
            )}
            <span className="block text-xs font-semibold">
              {open ? `${margin === "" ? "—" : margin}%` : "Hidden"}
            </span>
          </span>
        </span>
        <EditIcon className="app-muted shrink-0" sx={{ fontSize: 13 }} />
      </button>

      {open && (
        <div className="app-glass app-glass-rim absolute left-0 top-full z-20 mt-1.5 w-64 rounded-2xl border p-3.5">
          <p className="flex items-center gap-1.5 text-xs font-bold">
            <LockIcon className="app-muted" sx={{ fontSize: 13 }} />
            Margin · internal
          </p>
          <p className="app-muted mt-0.5 text-[11px] leading-snug">
            On screen now — press Esc or click away to hide it.
          </p>

          <div className="app-surface mt-2.5 flex rounded-full border p-1">
            <button
              type="button"
              onClick={() => {
                setUseCustom(false);
                setFormData((prev) => ({ ...prev, margin: DEFAULT_MARGIN }));
              }}
              className={segmentClasses(!useCustom)}
            >
              Default · {DEFAULT_MARGIN}%
            </button>
            <button
              type="button"
              onClick={() => setUseCustom(true)}
              className={segmentClasses(useCustom)}
            >
              Custom
            </button>
          </div>

          {useCustom && (
            <>
              <div className="relative mt-2">
                <input
                  type="number"
                  min="0"
                  max="99"
                  step="1"
                  autoFocus
                  value={margin ?? ""}
                  onChange={(event) => handleMarginChange(event.target.value)}
                  aria-label="Custom margin percentage"
                  aria-invalid={invalid}
                  className={`app-input w-full rounded-xl border py-2 pl-3 pr-7 text-sm ${
                    invalid ? "!border-rose-500" : ""
                  }`}
                />
                <span className="app-muted absolute right-2.5 top-1/2 -translate-y-1/2 text-xs">
                  %
                </span>
              </div>
              {invalid && (
                <p className="mt-1.5 text-[11px] leading-snug text-rose-600 dark:text-rose-300">
                  Enter 0–99, or close to keep {restoreRef.current}%.
                </p>
              )}
            </>
          )}

          <button
            type="button"
            onClick={closeEditor}
            className="app-btn app-btn-secondary app-btn-sm mt-3 w-full"
          >
            <VisibilityOffIcon sx={{ fontSize: 14 }} />
            Hide margin
          </button>
        </div>
      )}
    </div>
  );
};

export default MarginChip;
